import { useNavigation } from 'app/hooks/navigation';
import { GiftButton } from 'app/components/gift-button';
import {
  MOBILE_HOME_HEADER_BUTTON_HITSLOP_SIZE,
  MOBILE_HOME_HEADER_ICON_SIZE,
} from 'app/components/mobile-home-header-constants';
import {
  useReferralPrize202506Available,
  useReferralPrizeAvailable,
  useTrialFreeSubAvailable,
} from 'app/network/promotions';
import type { ColorValue } from 'react-native';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useTheme } from '@shopify/restyle';

import { navigateToIntro } from 'app/screens/intro/navigator-helper';
import { useUISize } from 'app/utils/web-helpers';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconSpeakerphone } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const dsTheme = createDSTheme();

type InviteButtonVisualType = 'defricheur' | 'trial' | 'gift' | 'speaker' | 'hidden';

function getInviteButtonVisualType({
  freeSubAvailable,
  inviteEnabled,
  referralPrize202506Available,
  referralPrizeAvailable,
}: {
  freeSubAvailable: string | false | undefined;
  inviteEnabled: boolean;
  referralPrize202506Available: boolean;
  referralPrizeAvailable: boolean;
}): InviteButtonVisualType {
  if (freeSubAvailable === 'defricheur') return 'defricheur';
  if (freeSubAvailable === 'trial') return 'trial';
  if (!inviteEnabled) return 'hidden';
  return referralPrizeAvailable || !referralPrize202506Available ? 'gift' : 'speaker';
}

function InviteButtonVisual({
  accessibilityLabel,
  buttonSize,
  color,
  iconSize,
  isSmallUI,
  onDefricheurPress,
  onInvitePress,
  onTrialPress,
  theme,
  type,
}: {
  accessibilityLabel: string;
  buttonSize: number;
  color: ColorValue;
  iconSize: number;
  isSmallUI: boolean;
  onDefricheurPress: () => void;
  onInvitePress: () => void;
  onTrialPress: () => void;
  theme: ReturnType<typeof useTheme>;
  type: InviteButtonVisualType;
}) {
  const giftButtonProps = { size: buttonSize, iconSize, accessibilityLabel };
  if (type === 'defricheur') return <GiftButton onPress={onDefricheurPress} {...giftButtonProps} />;
  if (type === 'trial') return <GiftButton onPress={onTrialPress} {...giftButtonProps} />;
  if (type === 'gift') return <GiftButton onPress={onInvitePress} {...giftButtonProps} />;
  if (type === 'hidden') return null;

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      onPress={onInvitePress}
      style={{
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: isSmallUI ? 0 : theme.spacing['spacing.general.x4'],
        marginLeft: isSmallUI ? 0 : 10,
      }}
    >
      <IconSpeakerphone color={color} size={iconSize} strokeWidth={ICON_STROKE_WIDTH} />
    </Pressable>
  );
}

export function InviteButton({
  color = dsTheme.colors['icon.primary'],
  buttonSize = MOBILE_HOME_HEADER_BUTTON_HITSLOP_SIZE,
  iconSize = MOBILE_HOME_HEADER_ICON_SIZE,
}: {
  color?: ColorValue;
  buttonSize?: number;
  iconSize?: number;
}) {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const theme = useTheme();
  const referralPrizeAvailable = useReferralPrizeAvailable();
  const referralPrize202506Available = useReferralPrize202506Available();
  const { isSmallUI } = useUISize();

  const { freeSubAvailable } = useTrialFreeSubAvailable()();

  const withAuthorize = useAuthorize();
  const showInviteScreen = () => withAuthorize(() => navigation.navigate('invite', undefined, { pop: true }));

  const accessibilityLabel = t('invite.headerAction');
  const type = getInviteButtonVisualType({
    freeSubAvailable,
    inviteEnabled: isFeatureEnabled(FeatureFlag.invite),
    referralPrize202506Available,
    referralPrizeAvailable,
  });

  return (
    <InviteButtonVisual
      accessibilityLabel={accessibilityLabel}
      buttonSize={buttonSize}
      color={color}
      iconSize={iconSize}
      isSmallUI={isSmallUI}
      onDefricheurPress={() => navigateToIntro(navigation, 'defricheur', 'offre_defricheur')}
      onInvitePress={showInviteScreen}
      onTrialPress={() => navigation.navigate('trialPromo', undefined, { pop: true })}
      theme={theme}
      type={type}
    />
  );
} // Never re-render
